import { useEffect, useState } from 'react';
import { CalendarDaysIcon, ListBulletIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { SignupSheet, CreateSheetPayload } from '@flock-types/signupSheets.types';

interface Props {
  createSheet: (payload: CreateSheetPayload) => Promise<SignupSheet>;
  onCreated: (sheetId: string) => void;
  onClose: () => void;
}

/**
 * New Sign-Up Sheet form (port of Expo signup-sheet/create.tsx). Only date
 * and item sheets are creatable — survey sheets render but aren't offered,
 * matching the Expo app.
 */
export default function SheetCreateModal({ createSheet, onCreated, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sheetType, setSheetType] = useState<'date' | 'item'>('date');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCreate = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const sheet = await createSheet({
        title: title.trim(),
        description: description.trim() || undefined,
        sheetType,
      });
      onCreated(sheet.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create sign-up sheet. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400';

  const typeCard = (
    type: 'date' | 'item',
    Icon: typeof CalendarDaysIcon,
    label: string,
    hint: string
  ) => {
    const selected = sheetType === type;
    return (
      <button
        onClick={() => setSheetType(type)}
        className={clsx(
          'flex flex-1 flex-col items-center rounded-xl border-2 px-4 py-4 transition',
          selected
            ? 'border-flock-navy-600 bg-flock-navy-50'
            : 'border-flock-gray-300 bg-white hover:border-flock-navy-400'
        )}
      >
        <Icon className={clsx('h-7 w-7', selected ? 'text-flock-navy-600' : 'text-flock-navy-400')} />
        <span
          className={clsx(
            'mt-2 text-sm font-semibold',
            selected ? 'text-flock-navy-800' : 'text-flock-navy-500'
          )}
        >
          {label}
        </span>
        <span className="mt-1 text-center text-xs text-flock-navy-400">{hint}</span>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">New Sign-Up Sheet</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Title *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              placeholder="e.g., Breakfast Sign-Up"
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Instructions or details for the group"
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">
              What kind of list?
            </label>
            <div className="flex gap-3">
              {typeCard('date', CalendarDaysIcon, 'Dates', 'Weekly rotation, schedule')}
              {typeCard('item', ListBulletIcon, 'Items', 'Supplies, tasks, roles')}
            </div>
          </div>
        </div>

        <button
          onClick={handleCreate}
          disabled={busy || !title.trim()}
          className="min-h-touch mt-5 w-full rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create Sign-Up Sheet'}
        </button>
      </div>
    </div>
  );
}
